import "@algolia/autocomplete-theme-classic/dist/theme.css";

import { AutocompleteState, BaseItem, createAutocomplete } from "@algolia/autocomplete-core";
import { getAlgoliaResults } from "@algolia/autocomplete-preset-algolia";
import { useQSSKey } from "@contexts/QueryStringStorageContext";
import { useQueryStringStorage } from "@hooks/useQueryStringStorage/useQueryStringStorage";
import analyticstracker from "@jmc/analyticstracker";
import Highlight from "@jmc/core/src/components/AutoComplete/Highlight/Highlight";
import { useLocale } from "@jmc/core/src/hooks/useLocale/index";
import { EventTypes } from "@jmc/core/src/types/EventTypes";
import { Icon, jnjSearch } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { TextField } from "@jmc/solid-design-system/src/components/atoms/TextField/TextField";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { BreakPoint, useMediaQuery } from "@jmc/solid-design-system/src/hooks/useMediaQuery/useMediaQuery";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { debounce } from "@jmc/utils/utils/debounce";
import { mdiClose, mdiMagnify } from "@mdi/js";
import type { SearchClient } from "algoliasearch/lite";
import classnames from "classnames";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchBox } from "react-instantsearch";

import style from "./style.module.scss";

type Props = {
    searchClient: SearchClient;
    placeholder?: string;
    indexName: string;
    localeFilter?: boolean;
    setQuery?: (v: string) => void;
    attribute?: "query" | "title" | "page_title";
    variant?: "default" | "big";
    initialQuery?: string;
    contentType?: string;
};

const debouncePromise = (fn, time): Promise<unknown> => {
    let timerId = undefined as NodeJS.Timeout;

    return (...args) => {
        if (timerId) {
            clearTimeout(timerId);
        }

        return new Promise((resolve) => {
            timerId = setTimeout(() => resolve(fn(...args)), time);
        });
    };
};

export const Autocomplete = ({
    searchClient,
    placeholder,
    indexName,
    localeFilter = true,
    setQuery = null,
    attribute = "title",
    variant = "default",
    initialQuery = null,
    contentType = null,
}: Props): JSX.Element => {
    const { jnjFullBranded } = useJnjBranding();

    const { t } = useTranslation();
    const formRef = React.useRef(null);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const debounceItems = debouncePromise((items: unknown) => Promise.resolve(items), 300);
    const [autocompleteState, setAutocompleteState] = useState({} as AutocompleteState<BaseItem>);
    const { refine, query } = useSearchBox();
    const locale = useLocale();
    const qss = useQueryStringStorage();
    const hashKey = useQSSKey();
    const isMobile = useMediaQuery(BreakPoint.md);

    useEffect(() => {
        if (initialQuery) {
            autocomplete.setQuery(initialQuery);
            setQuery && setQuery(initialQuery);
        }
    }, [initialQuery]);

    const onSelect = (query: string): void => {
        onSelectCallback(query);
    };

    const debounceRefine = useCallback(
        debounce((query: unknown) => {
            refine(query);
        }, 300),
        [],
    );

    useEffect(() => {
        qss.storeSingle(hashKey, "q", query);

        if (query && query !== "") {
            const tracking = setTimeout(() => {
                analyticstracker().trackEvent({
                    event: EventTypes.SEARCH_QUERY,
                    info: {
                        keyword: query,
                        type: contentType,
                    },
                });
            }, 1000);

            return (): void => {
                clearTimeout(tracking);
            };
        }
    }, [query]);

    useEffect(() => {
        const qs = qss.getQS(hashKey);
        qs &&
            qs.forEach((value, key) => {
                if (key === "q" && value !== "") {
                    autocomplete.setQuery(value);
                    debounceRefine(value);
                }
            });
    }, []);

    const onSelectCallback = useCallback((value: string): void => {
        autocomplete.getFormProps({ inputElement: inputRef.current }).onReset(new Event("click"));
        autocomplete.setQuery(value);
    }, []);

    const autocomplete = useMemo(() => {
        return createAutocomplete({
            onStateChange({ state }) {
                setAutocompleteState(state);
                (state?.query === "" || state?.query?.length > 2) && debounceRefine(state?.query);
            },
            getSources() {
                return debounceItems([
                    {
                        sourceId: "autocompleteResults",
                        getItemInputValue({ item }) {
                            return item?.title;
                        },
                        getItems({ query }) {
                            return query?.length > 2
                                ? getAlgoliaResults({
                                      searchClient,
                                      queries: [
                                          {
                                              indexName: indexName,
                                              query,
                                              filters:
                                                  localeFilter && contentType
                                                      ? `locale:${locale} AND content_type:'${contentType}'`
                                                      : localeFilter && !contentType
                                                      ? `locale:${locale}`
                                                      : contentType && !localeFilter
                                                      ? `content_type:'${contentType}'`
                                                      : "",
                                              params: {
                                                  hitsPerPage: 10,
                                                  highlightPreTag: "<mark>",
                                                  highlightPostTag: "</mark>",
                                              },
                                          },
                                      ],
                                  })
                                : [];
                        },
                        templates: {
                            item({ item }) {
                                return item?.[attribute];
                            },
                            noResults() {
                                return "";
                            },
                        },
                        onSelect(params: { setIsOpen: (arg0: boolean) => void; item: { [x: string]: string } }) {
                            params.setIsOpen(false);
                            onSelect(params?.item?.[attribute] as string);
                        },
                    },
                ]);
            },
            // plugins,
            detachedMediaQuery: "none",
            openOnFocus: false,
        });
    }, [indexName]);

    const handleOnFocus = (e: Event): void => {
        const { onFocus } = autocomplete.getInputProps({});
        onFocus(e);
    };

    const handleOnBlur = (): void => {
        const { onBlur } = autocomplete.getInputProps({});
        autocomplete.setIsOpen(false);
        onBlur();
    };

    const handleOnChange = (e: Event): void => {
        const { onChange } = autocomplete.getInputProps({});
        if (setQuery) setQuery(inputRef?.current?.value);
        onChange(e);
    };

    return (
        <>
            <div
                className={classnames(jnjFullBranded ? undefined : "aa-Autocomplete", style.container, style[variant])}
                {...autocomplete.getRootProps({})}
            >
                <form
                    ref={formRef}
                    className={classnames(jnjFullBranded ? undefined : "aa-Form", style.form)}
                    {...autocomplete.getFormProps({ inputElement: inputRef.current })}
                    onSubmit={(e: React.FormEvent) => {
                        e.preventDefault();
                        const query = (autocomplete.getInputProps({}) as unknown as { value: string }).value;
                        if (query !== "") {
                            autocomplete.getFormProps({ inputElement: inputRef.current }).onSubmit(e);
                            autocomplete.getFormProps({ inputElement: inputRef.current }).onReset(e);
                            autocomplete.setQuery(query);
                        }
                    }}
                >
                    <div className={classnames("aa-InputWrapper", style.inputWrapper)}>
                        <TextField
                            ref={inputRef}
                            className={classnames("aa-Input", style.input)}
                            border={false}
                            {...autocomplete.getInputProps({})}
                            onFocus={(e: Event) => handleOnFocus(e)}
                            onBlur={() => handleOnBlur()}
                            onChange={(e: Event) => handleOnChange(e)}
                            placeholder={placeholder}
                            type="text"
                            startAdornment={
                                <>
                                    <Icon
                                        onClick={() => {
                                            inputRef.current.focus();
                                        }}
                                        color={
                                            jnjFullBranded
                                                ? "interactive-02"
                                                : autocomplete.getInputProps({}).value === ""
                                                ? "text-grey"
                                                : "grey-light"
                                        }
                                        type={jnjFullBranded ? "jnj" : "mdi"}
                                        icon={jnjFullBranded ? jnjSearch : mdiMagnify}
                                        size={jnjFullBranded ? "medium" : variant === "big" ? "medium" : "small"}
                                        data-test-id={`search-box-search-icon`}
                                        className={jnjFullBranded ? style.icon : null}
                                        verticalAlign="middle"
                                    />
                                </>
                            }
                            endAdornment={
                                <>
                                    {autocomplete.getInputProps({}).value !== "" && (
                                        <div className={classnames("aa-InputWrapperSuffix", style.suffix)}>
                                            <button
                                                className={classnames("aa-ClearButton", style.clearSearch)}
                                                data-test-id={`search-box-close-icon`}
                                                type="reset"
                                                aria-label={t("Clear search", { ns: "search" })}
                                            >
                                                <Icon size="small" color="grey-light" icon={mdiClose} />
                                            </button>
                                        </div>
                                    )}
                                </>
                            }
                        />
                    </div>
                </form>

                {autocompleteState.isOpen &&
                    autocompleteState.collections.map((collection, index) => {
                        const { source, items } = collection;

                        return (
                            <div
                                ref={dropdownRef}
                                key={`source-${index}`}
                                className={classnames("aa-Panel", style.panel)}
                                {...autocomplete.getPanelProps({})}
                            >
                                <div className="aa-Source">
                                    <div className={style.panelTitle}>
                                        <Typography
                                            font="title"
                                            size={jnjFullBranded ? "xl" : isMobile ? "3xl" : "l"}
                                            color={jnjFullBranded ? "light" : "dark"}
                                            weight={jnjFullBranded ? "400" : "500"}
                                        >
                                            {t("Suggestions", { ns: "search" })}
                                        </Typography>
                                    </div>
                                    {items.length > 0 && (
                                        <ul className="aa-List" {...autocomplete.getListProps()}>
                                            {items.map((item) => (
                                                <li
                                                    key={item.objectID}
                                                    className={classnames("aa-Item", style.item)}
                                                    {...autocomplete.getItemProps({
                                                        item,
                                                        source,
                                                    })}
                                                >
                                                    <Highlight
                                                        attribute={item?._highlightResult?.query ? "query" : attribute}
                                                        item={item}
                                                    />
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        );
                    })}
            </div>
        </>
    );
};

export default Autocomplete;
